import { API_BASE_URL } from "../apiConfig";
import React, { useState, useEffect, useCallback } from "react";
import "./RecyclingControl.css";

const itemIcons = {
  Plastic: "PL",
  Cardboard: "CB",
  Paper: "PA",
  "Metal Can": "MC",
  Glass: "GL",
};

const getIcon = (name) => itemIcons[name] || "RC";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const RecyclingControl = () => {
  const [summary, setSummary] = useState({ unnotified: [], notified: [] });
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, compRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/recycling/summary`),
        fetch(`${API_BASE_URL}/api/recycling/companies`),
      ]);

      const sumData = await sumRes.json();
      const compData = await compRes.json();
      setSummary({
        unnotified: Array.isArray(sumData.unnotified) ? sumData.unnotified : [],
        notified: Array.isArray(sumData.notified) ? sumData.notified : [],
      });
      setCompanies(Array.isArray(compData) ? compData : []);
    } catch (err) {
      console.error("Error fetching recycling data:", err);
      showToast("Failed to load recycling data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNotify = async (itemType) => {
    const companyId = selectedCompany[itemType] || "all";

    setNotifying((prev) => ({ ...prev, [itemType]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/recycling/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemType, companyId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Notification failed");

      showToast(data.message, "success");
      fetchData();
    } catch (err) {
      showToast(err.message || "Notification failed", "error");
    } finally {
      setNotifying((prev) => ({ ...prev, [itemType]: false }));
    }
  };

  return (
    <div className="rc-page">
      {toast && <div className={`rc-toast rc-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="rc-header">
        <div>
          <h1>Recycling Control</h1>
          <p>Manage detected recyclable items and notify recycling companies.</p>
        </div>
      </div>

      {loading ? (
        <div className="rc-loading">Loading recyclable items data...</div>
      ) : (
        <div className="rc-body">
          <div className="rc-left">
            <div className="rc-section-title">
              <span className="rc-dot green" />
              Items Detected (Pending Notification)
              <span className="rc-count-badge">{summary.unnotified.length}</span>
            </div>

            {summary.unnotified.length === 0 ? (
              <div className="rc-empty">
                <span>OK</span>
                <p>All detected items have been notified.</p>
              </div>
            ) : (
              <div className="rc-items-grid">
                {summary.unnotified.map((item) => (
                  <div key={item.itemType} className="rc-item-card">
                    <div className="rc-item-icon">{getIcon(item.itemType)}</div>
                    <div className="rc-item-info">
                      <h3>{item.itemType}</h3>
                      <span className="rc-qty-badge">
                        Qty: {item.quantity} item{item.quantity > 1 ? "s" : ""}
                      </span>
                      <span className="rc-complaints-count">
                        Across {item.complaintIds?.length || 1} complaint
                        {item.complaintIds?.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="rc-item-actions">
                      <select
                        className="rc-company-select"
                        value={selectedCompany[item.itemType] || "all"}
                        onChange={(e) =>
                          setSelectedCompany((prev) => ({
                            ...prev,
                            [item.itemType]: e.target.value,
                          }))
                        }
                      >
                        <option value="all">All Companies</option>
                        {companies.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.companyName || company.username}
                          </option>
                        ))}
                      </select>
                      <button
                        className="rc-notify-btn"
                        onClick={() => handleNotify(item.itemType)}
                        disabled={notifying[item.itemType]}
                      >
                        {notifying[item.itemType] ? "Sending..." : "Notify"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rc-right">
            <div className="rc-section-title">
              <span className="rc-dot orange" />
              Active Notifications (Pending Pickup)
              <span className="rc-count-badge orange">{summary.notified.length}</span>
            </div>

            {summary.notified.length === 0 ? (
              <div className="rc-empty">
                <span>--</span>
                <p>No active pickups right now.</p>
              </div>
            ) : (
              <div className="rc-notif-list">
                {summary.notified.map((notification) => (
                  <div key={notification._id} className="rc-notif-card">
                    <div className="rc-notif-icon">{getIcon(notification.itemType)}</div>
                    <div className="rc-notif-details">
                      <div className="rc-notif-item-name">{notification.itemType}</div>
                      <div className="rc-notif-meta">
                        <span>Qty: {notification.quantity}</span>
                        <span>
                          Location: {notification.location?.lat?.toFixed(4)},{" "}
                          {notification.location?.lng?.toFixed(4)}
                        </span>
                      </div>
                      <div className="rc-notif-company">Company: {notification.companyName}</div>
                      <div className="rc-notif-date">{formatDate(notification.createdAt)}</div>
                    </div>
                    <div className="rc-notif-status">
                      <span className="rc-status-pill pending">Pending Pickup</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecyclingControl;
